import React, { useState } from "react";
import marked from "marked";
import styled from "styled-components";
import { useIntl } from "gatsby-plugin-intl";
import { useStaticQuery, graphql } from "gatsby";
import { CSSTransition } from "react-transition-group";
import get from "lodash/get";

import DownCaret from "../downCaret";

const StyledWrapper = styled.div`
  width: 100%;
  background-color: ${(props) => props.theme.color[props.backgroundColor]};
  color: ${(props) => props.theme.color[props.textColor]};
  display: flex;
  flex-direction: column;
  padding: 110px 16px 20px 16px;

  ${(props) => props.theme.responsive.desktop} {
    padding: 110px 10% 40px 10%;
  }
  ${(props) => props.theme.responsive.fullhd} {
    padding: 110px 20% 40px 20%;
  }
`;

const StyledTitle = styled.div`
  font-size: 1.1rem;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 2rem;
  text-align: center;
`;

const StyledFAQDropdown = styled.div`
  position: relative;
  margin-bottom: 1rem;
  font-family: ${(props) => props.theme.font.sans};
  background-color: ${(props) => props.theme.color.white};
  border-radius: 3px;
`;

const StyledFAQButton = styled.div`
  font-size: 1.1rem;
  padding: 2rem;
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => props.theme.color.white};

  &:hover {
    cursor: pointer;
    background-color: ${(props) => props.theme.color.lightGrey};
  }
`;

const StyeldFaqTitle = styled.div``;

const StyledCaretWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledContentWrapper = styled.div`
  opacity: 0;
  max-height: 0rem;
  display: flex;
  overflow: hidden;

  &.dropdownOpen-enter {
    opacity: 0;
    max-height: 0rem;
  }

  &.dropdownOpen-enter-done {
    opacity: 1;
    max-height: 80rem;
    transition: all 0.7s;
  }

  &.dropdownOpen-exit {
    opacity: 1;
    max-height: 80rem;
  }

  &.dropdownOpen-exit-done {
    opacity: 0;
    max-height: 0rem;
    transition: all 0.3s;
  }
`;

const StyledFAQContent = styled.div`
  padding: 2rem 2rem 2rem 2rem;
`;

const StyledFAQPost = styled.div`
  font-size: 0.8rem;
  line-height: 1.2rem;
  color: ${(props) => props.theme.color.dimGrey};
`;

const FAQDropdown = ({ faq, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const intl = useIntl();

  return (
    <StyledFAQDropdown>
      <StyledFAQButton onClick={() => setIsOpen(!isOpen)}>
        <StyeldFaqTitle>{faq.question[intl.locale]}</StyeldFaqTitle>
        <StyledCaretWrapper>
          <DownCaret isOpen={isOpen} />
        </StyledCaretWrapper>
      </StyledFAQButton>
      <CSSTransition in={isOpen} key="dropdownOpen" classNames="dropdownOpen">
        <StyledContentWrapper className="dropdownOpen">
          <StyledFAQContent>
            <StyledFAQPost>
              <div
                dangerouslySetInnerHTML={{
                  __html: marked(faq?.answer?.[intl.locale] || ""),
                }}
              />
            </StyledFAQPost>
          </StyledFAQContent>
        </StyledContentWrapper>
      </CSSTransition>
    </StyledFAQDropdown>
  );
};

const FaqModal = ({ title, faqs, backgroundColor, textColor }) => {
  const intl = useIntl();

  return (
    <StyledWrapper
      textColor={get(textColor, "color", undefined)}
      backgroundColor={get(backgroundColor, "color", undefined)}
    >
      {title && <StyledTitle>{title[intl.locale]}</StyledTitle>}
      {faqs.map((faq) => {
        return <FAQDropdown key={faq.question} faq={faq} />;
      })}
    </StyledWrapper>
  );
};

export default FaqModal;

export const query = graphql`
  fragment faqModal on STRAPI_ComponentBaseFaqModal {
    __typename
    id
    title {
      en
      es
      it
    }
    faqs {
      question {
        en
        es
        it
      }
      answer {
        en
        es
        it
      }
    }
    backgroundColor {
      color
    }
    textColor {
      color
    }
  }
`;
